import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Filter, X, Eye, Download, Printer, Trash2, AlertTriangle, Edit, CheckCircle, Navigation } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';
import OptimizedRouteModal from './OptimizedRouteModal';

const buildColumns = (ui) => [
  { field: 'refno', title: ui('columns.refNo'), widthClass: 'w-[9%]', hideable: true },
  { field: 'date', title: ui('columns.date'), widthClass: 'w-[8%]', hideable: true },
  { field: 'drivername', title: ui('columns.driver'), widthClass: 'w-[12%]', hideable: true },
  { field: 'vehiclenumber', title: ui('columns.vehicle'), widthClass: 'w-[12%]', hideable: true },
  { field: 'status', title: ui('common.status'), widthClass: 'w-[9%]', hideable: true },
  { field: 'remarks', title: ui('columns.remarks'), widthClass: 'w-[14%]', hideable: true },
  { field: 'createdby', title: ui('columns.createdBy'), widthClass: 'w-[10%]', hideable: true },
  { field: 'actions', title: ui('columns.options'), widthClass: 'w-[12%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const formatVehicle = (note) => {
  if (!note?.Vehicle) return '-';
  const { Number: num, Model: model } = note.Vehicle;
  return model ? `${num} (${model})` : num || '-';
};

const getCellValue = (note, field) => {
  switch (field) {
    case 'refno':
      return note.RefNo || '-';
    case 'date':
      return formatDate(note.Date);
    case 'drivername':
      return note.Driver?.Name || '-';
    case 'vehiclenumber':
      return note.Vehicle?.Number || '-';
    case 'status':
      return note.Status || '-';
    case 'remarks':
      return note.Remarks || '-';
    case 'createdby':
      return note.CreatedName || '-';
    default:
      return note[field];
  }
};

const statusColors = {
  CONFIRMED: 'bg-blue-100 text-blue-800',
  NEW: 'bg-yellow-100 text-yellow-800',
  FINISHED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
};

const DeliveryNotes = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const NOTE_COLUMNS = useMemo(() => buildColumns(ui), [ui]);
  const navigate = useNavigate();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [cardFilters, setCardFilters] = useState({ DriverId: '', Status: '', Keyword: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => defaultVisibility(buildColumns(getAppUi('en'))));
  const [groupField, setGroupField] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [routeNote, setRouteNote] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = defaultVisibility(NOTE_COLUMNS);
      NOTE_COLUMNS.forEach((col) => {
        if (prev[col.field] !== undefined) next[col.field] = prev[col.field];
      });
      return next;
    });
  }, [NOTE_COLUMNS]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadDrivers = async () => {
    try {
      const response = await MainService.DriversInDriverAllocation();
      if (response && Array.isArray(response)) {
        setDrivers(response);
      }
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  };

  const fetchNotes = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    cardFilters: cardFiltersArg,
    filtersApplied: filtersAppliedArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const applied = filtersAppliedArg !== undefined ? filtersAppliedArg : filtersApplied;
    const cf = cardFiltersArg ?? cardFilters;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        Keyword: applied && cf.Keyword?.trim() ? cf.Keyword.trim() : null,
        Status: applied && cf.Status ? cf.Status : null,
        DriverId: applied && cf.DriverId ? parseInt(cf.DriverId, 10) : null,
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService.DeliveryNotesListList(body);
      if (seq !== loadSeqRef.current) return;
      setNotes(Array.isArray(response?.Data) ? response.Data : []);
      setTotalCount(Number(response?.Total) || 0);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Error loading delivery notes:', err);
      setError('Failed to load delivery notes');
      setNotes([]);
      setTotalCount(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, cardFilters, filtersApplied, sortField, sortDirection, filtersByField]);

  useEffect(() => {
    loadDrivers();
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => NOTE_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [NOTE_COLUMNS, columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows: notes }];
    const mapped = notes.map((r) => ({ ...r, [groupField]: getCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [notes, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const handleApplyFilters = () => {
    setFiltersApplied(true);
    setPage(1);
    fetchNotes({ page: 1, filtersApplied: true });
  };

  const handleClearFilters = () => {
    const cleared = { DriverId: '', Status: '', Keyword: '' };
    setCardFilters(cleared);
    setFiltersApplied(false);
    setPage(1);
    fetchNotes({ page: 1, cardFilters: cleared, filtersApplied: false });
  };

  const handleRefresh = () => {
    const cleared = { DriverId: '', Status: '', Keyword: '' };
    setPage(1);
    setCardFilters(cleared);
    setFiltersApplied(false);
    setShowFilters(false);
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchNotes({
      page: 1,
      cardFilters: cleared,
      filtersApplied: false,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
      fetchNotes({ page: 1, pageSize: 'all' });
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
      fetchNotes({ page: prev.page, pageSize: prev.pageSize });
    }
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchNotes({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchNotes({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchNotes({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = NOTE_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchNotes({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchNotes({ page: 1, filters: nextFilters });
  };

  const clearSortAndFilters = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    setPage(1);
    if (prev && prev.pageSize !== pageSize) {
      setPageSize(prev.pageSize);
    }
    fetchNotes({
      page: 1,
      pageSize: prev?.pageSize ?? pageSize,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Delivery Notes',
        fileName: `delivery_notes_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...NOTE_COLUMNS.filter((c) => c.field !== 'actions').map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: NOTE_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (note, serial) => [
          serial,
          ...NOTE_COLUMNS.filter((c) => c.field !== 'actions').map((c) =>
            c.field === 'vehiclenumber' ? formatVehicle(note) : getCellValue(note, c.field)
          ),
        ],
      });
      setSuccess('Excel file downloaded successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      if (err?.message === 'No rows to export') {
        setError('No data to export');
      } else {
        setError('Failed to export to Excel');
      }
    }
  };

  const handleView = async (note) => {
    try {
      setLoading(true);
      const response = await MainService.GetDeliveryNoteById(note.Id);
      if (response) {
        setSelectedNote(response);
        setViewModalOpen(true);
      } else {
        setError('Failed to load delivery note details');
      }
    } catch (err) {
      console.error('Error loading delivery note:', err);
      setError('Failed to load delivery note details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (note) => {
    try {
      setLoading(true);
      const response = await MainService.PrintDeliveryNoteAllocation(note.Id);
      if (response && response != null) {
        const printUrl = `${getApiBaseUrl()}ReportFiles/${response}`;
        window.open(printUrl, '_blank');
      } else {
        setError(ui('delivery.notes.printError'));
      }
    } catch (err) {
      console.error('Error printing delivery note:', err);
      setError(ui('delivery.notes.printError'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewRoute = (note) => {
    setRouteNote(note);
    setRouteModalOpen(true);
  };

  const handleEdit = (note) => {
    navigate('/home/delivery/allocation', {
      state: { deliveryNoteId: note.Id, driverId: note.DriverId },
    });
  };

  const handleDelete = (note) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!noteToDelete) return;

    if (noteToDelete.Status === 'FINISHED') {
      setError('Cannot delete - Delivery note is already finished');
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.DeleteDeliveryNote(noteToDelete.Id);
      if (response) {
        setSuccess('Delivery note deleted successfully');
        await fetchNotes({ page });
      } else {
        setError('Failed to delete delivery note');
      }
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
    } catch (err) {
      console.error('Error deleting delivery note:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  };

  const renderCell = (note, column) => {
    if (column.field === 'vehiclenumber') {
      return formatVehicle(note);
    }
    if (column.field === 'status') {
      return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[note.Status] || 'bg-gray-100 text-gray-800'}`}>
          {note.Status || '-'}
        </span>
      );
    }
    if (column.field === 'remarks') {
      const text = note.Remarks || '-';
      return (
        <span className="block max-w-xs truncate" title={note.Remarks || ''}>
          {text}
        </span>
      );
    }
    if (column.field === 'actions') {
      return (
        <div className="flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => handleView(note)}
            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            title={ui('common.viewDetails')}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleViewRoute(note)}
            className="p-2 text-teal-700 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors"
            title="View / optimize route"
          >
            <Navigation className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleEdit(note)}
            className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
            title={ui('common.edit')}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handlePrint(note)}
            disabled={loading}
            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={ui('common.print')}
          >
            <Printer className="w-4 h-4" />
          </button>
          {note.Status !== 'FINISHED' && (
            <button
              type="button"
              onClick={() => handleDelete(note)}
              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              title={ui('common.delete')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      );
    }
    return getCellValue(note, column.field);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'deliveryNotes')}</h1>
            <p className="text-sm text-gray-500 mt-1">{ui('delivery.notes.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showFilters ? 'bg-primary text-white hover:bg-primary-dark' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              {ui('common.filters')}
            </button>
            <button
              type="button"
              onClick={exportToExcel}
              disabled={notes.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {ui('common.exportExcel')}
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {ui('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.driver')}</label>
              <select
                value={cardFilters.DriverId}
                onChange={(e) => setCardFilters((prev) => ({ ...prev, DriverId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{ui('delivery.notes.allDrivers')}</option>
                {drivers.map((driver) => (
                  <option key={driver.Id} value={driver.Id}>{driver.Name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{ui('common.status')}</label>
              <select
                value={cardFilters.Status}
                onChange={(e) => setCardFilters((prev) => ({ ...prev, Status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{ui('delivery.notes.allStatus')}</option>
                <option value="CONFIRMED">{ui('delivery.notes.statusConfirmed')}</option>
                <option value="NEW">{ui('delivery.notes.statusNew')}</option>
                <option value="FINISHED">{ui('delivery.notes.statusFinished')}</option>
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{ui('delivery.notes.searchRefLabel')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={cardFilters.Keyword}
                  onChange={(e) => setCardFilters((prev) => ({ ...prev, Keyword: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                  placeholder={ui('delivery.notes.searchRefPlaceholder')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="md:col-span-2 flex items-end gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex-1"
              >
                {ui('common.apply')}
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex-1"
              >
                {ui('common.clear')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{ui('delivery.notes.listTitle')}</h2>
            {totalCount > 0 && (
              <span className="text-sm text-gray-600">{ui('delivery.notes.totalCount', { total: totalCount })}</span>
            )}
          </div>
        </div>

        <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">{ui('delivery.notes.loading')}</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${NOTE_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={NOTE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="w-full min-w-[1100px] table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-[4%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {ui('columns.sNo')}
                </th>
                {NOTE_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={NOTE_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {notes.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg mb-2">{ui('delivery.notes.noNotes')}</p>
                    <p className="text-sm">{ui('delivery.notes.noNotesHint')}</p>
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={NOTE_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((note, index) => {
                      serial += 1;
                      block.push(
                        <tr key={note.Id || `${group.key}-${index}`} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-600">
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {NOTE_COLUMNS.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`whitespace-nowrap px-4 py-2 text-sm text-gray-600 ${
                                  column.field === 'refno' ? 'font-medium text-gray-900' : ''
                                } ${column.align === 'center' ? 'text-center' : ''}`}
                              >
                                {renderCell(note, column)}
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0
                  ? ui('delivery.notes.totalCount', { total: 0 })
                  : ui('delivery.notes.showingToOf', { from: rangeFrom, to: rangeTo, total: totalCount })}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField ? ` · grouped by ${NOTE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}` : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {viewModalOpen && selectedNote && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-semibold text-gray-900">{ui('delivery.notes.detailsTitle')}</h3>
              <button
                type="button"
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedNote(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.refNo')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.RefNo || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.date')}</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedNote.Date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.driver')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.Driver?.Name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.vehicle')}</label>
                  <p className="text-sm text-gray-900">{formatVehicle(selectedNote)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('common.status')}</label>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[selectedNote.Status] || 'bg-gray-100 text-gray-800'}`}>
                    {selectedNote.Status || '-'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.createdBy')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.CreatedName || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.remarks')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.Remarks || '-'}</p>
                </div>
              </div>
              {selectedNote.DeliveryNoteDetails && selectedNote.DeliveryNoteDetails.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">{ui('delivery.notes.ordersInNote', { count: selectedNote.DeliveryNoteDetails.length })}</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">{ui('columns.voucherNo')}</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">{ui('columns.customer')}</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">{ui('columns.amount')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedNote.DeliveryNoteDetails.map((detail) => (
                          <tr key={detail.Id}>
                            <td className="px-4 py-2 text-gray-600">{detail.Order?.VoucherNo || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">{detail.Order?.Customer?.Name || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">
                              {detail.Order?.CustomerAmount !== null && detail.Order?.CustomerAmount !== undefined
                                ? `AED ${parseFloat(detail.Order.CustomerAmount).toFixed(2)}`
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedNote(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {ui('common.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {deleteDialogOpen && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative z-[100000]">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 -mx-6 -mt-6 rounded-t-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">{ui('delivery.notes.confirmDeleteTitle')}</h3>
              </div>
            </div>
            <div className="py-4">
              <p className="text-gray-700 mb-2">
                {ui('delivery.notes.confirmDeleteMessage', { refNo: noteToDelete?.RefNo || 'N/A' })}
              </p>
              <p className="text-sm text-gray-500">{ui('delivery.notes.confirmDeleteWarning')}</p>
            </div>
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ui('common.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{ui('delivery.notes.deleting')}</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>{ui('common.delete')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <OptimizedRouteModal
        open={routeModalOpen}
        mode="view"
        onClose={() => {
          setRouteModalOpen(false);
          setRouteNote(null);
        }}
        deliveryNoteId={routeNote?.Id}
        deliveryNoteLabel={
          routeNote
            ? `${routeNote.RefNo || 'DN'} · ${routeNote.Driver?.Name || ''}`
            : ''
        }
        onConfirm={() => {
          setSuccess('Delivery route saved');
        }}
      />
    </div>
  );
};

export default DeliveryNotes;
